import React from 'react';
import PropTypes from 'prop-types';

/**
 * IGEA: Course Content card with embedded progress bar.
 * Title + optional description + progress bar + % + encouragement.
 */
// Tier the encouragement copy by completion %, so the line below the
// progress bar actually reflects how far the learner has come instead
// of always reading the same generic "Continua così". Each tier pairs
// a phrase with an emoji whose meaning matches the phrase (rocket for
// "start", muscle for "effort", target for "approaching goal", trophy
// for "finish"). The mapping intentionally avoids pure decoration.
const encouragementFor = (pct) => {
  if (pct >= 100) { return { text: 'Corso completato', emoji: '🏆', label: 'trofeo' }; }
  if (pct >= 76) { return { text: 'Quasi al traguardo', emoji: '🎯', label: 'obiettivo' }; }
  if (pct >= 51) { return { text: 'Sei oltre metà corso', emoji: '🔥', label: 'momentum' }; }
  if (pct >= 26) { return { text: 'Stai facendo progressi', emoji: '💪', label: 'forza' }; }
  if (pct >= 1) { return { text: 'Buon inizio', emoji: '✨', label: 'scintilla' }; }
  return { text: 'Inizia il tuo percorso', emoji: '🚀', label: 'parti' };
};

const IgeaProgressCard = ({
  title = 'Contenuto del corso',
  description = '',
  completed = 0,
  total = 0,
  propedeuticita = false,
}) => {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const lessonsLabel = total === 1 ? 'lezione completata' : 'lezioni completate';
  const encouragement = encouragementFor(pct);

  return (
    <div>
      {/* Segnalazione cliente (2026-08-11): "lo mostrerei da qualche parte
          che l'utente sta seguendo il corso in modalita' ecm". Senza questo
          i lucchetti sui moduli e il conto alla rovescia sul pulsante
          Avanti (vedi IgeaMainContent) sembrano un guasto a chi non sa di
          essere su un percorso accreditato — lo stesso motivo per cui il
          vincolo, quando resta chiuso senza risposta dall'unita', ora dice
          perche'.
          Va qui e non nel riquadro del contenuto (IgeaMainContent): questa
          e' l'unica intestazione a livello di CORSO, e `propedeuticita'` e'
          una proprieta' del corso/iscrizione, non della singola lezione
          (vedi il commento su IgeaPlayerLayout). Mettercela nel riquadro
          video l'avrebbe ripetuta a ogni lezione E le avrebbe sottratto
          spazio verticale — lo stesso problema del contenuto tagliato
          risolto sopra.
          Compare SOLO quando `propedeuticita'` e' vero: quel valore parte
          `false` sia per un corso davvero libero sia finche' nessuna unita'
          ha ancora annunciato `igea:unit-gate` (vedi IgeaPlayerLayout) — nei
          due casi il silenzio e' la scelta giusta, un'etichetta "percorso
          libero" mostrata di default sarebbe stata rumore per il primo caso
          e un'affermazione falsa, anche se transitoria, per il secondo. */}
      {propedeuticita && (
        <div
          className="tw-inline-flex tw-items-center tw-gap-1 tw-mb-2 tw-px-2 tw-py-0.5 tw-rounded-full"
          style={{
            fontSize: '11px', fontWeight: 600, color: '#7C3AED', background: '#F5F3FF',
          }}
        >
          <span aria-hidden="true">🎓</span>
          <span>Modalità ECM</span>
        </div>
      )}
      <h3
        className="tw-mb-1 tw-m-0"
        style={{ fontSize: '15px', fontWeight: 600, color: '#1F2937' }}
      >
        {title}
      </h3>
      {propedeuticita && (
        <p
          className="tw-leading-relaxed tw-mb-2 tw-m-0"
          style={{ fontSize: '11px', color: '#6B7280' }}
        >
          Corso accreditato: le lezioni si sbloccano in ordine, senza salti.
        </p>
      )}
      {/* Sotto `md` la descrizione e la riga d'incoraggiamento non si vedono:
          lo spazio in altezza e' della lezione (igea-frontend #239). */}
      {description && (
        <p
          className="tw-hidden md:tw-block tw-leading-relaxed tw-mb-3 tw-m-0"
          style={{ fontSize: '12px', color: '#6B7280' }}
        >
          {description}
        </p>
      )}
      {/* Progress block.
          No top divider — the surrounding sidebar already gives visual
          separation, and a horizontal rule here was reading as "card
          inside card" inside the unified white panel. */}
      <div className="tw-mt-3">
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <span style={{ fontSize: '12px', color: '#6B7280' }}>Il tuo progresso</span>
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#1F2937' }}>
            {completed} di {total} {lessonsLabel}
          </span>
        </div>
        <div className="tw-h-1 tw-rounded-full tw-overflow-hidden tw-mb-2" style={{ background: '#E5E7EB' }}>
          <div
            className="tw-h-full tw-rounded-full tw-transition-all"
            style={{
              width: `${pct}%`,
              background: 'linear-gradient(90deg, #1B9F5E 0%, #7C3AED 100%)',
            }}
          />
        </div>
        <div className="tw-hidden md:tw-flex tw-items-center tw-justify-between">
          <span style={{ fontSize: '12px', color: '#6B7280' }}>{pct}% completato</span>
          <span
            className="tw-flex tw-items-center tw-gap-1"
            style={{ fontSize: '12px', fontWeight: 500, color: '#7C3AED' }}
          >
            {encouragement.text}{' '}
            <span role="img" aria-label={encouragement.label}>{encouragement.emoji}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

IgeaProgressCard.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  completed: PropTypes.number,
  total: PropTypes.number,
  // Vero solo per le iscrizioni in modalita' ECM (vedi IgeaPlayerLayout).
  // Falso sia sul percorso libero sia finche' non e' ancora noto — i due
  // casi non si distinguono qui apposta, in entrambi non c'e' niente da
  // mostrare.
  propedeuticita: PropTypes.bool,
};

IgeaProgressCard.defaultProps = {
  title: 'Contenuto del corso',
  description: '',
  completed: 0,
  total: 0,
  propedeuticita: false,
};

export default IgeaProgressCard;
